import { useEffect, useState } from 'react';
import { openPropertyGuestGuide } from '@/events/propertyGuestGuide';
import { useFavorites } from '@/hooks/useFavorites';
import type { StickyBookBarData } from '../types';

type Props = {
  data: StickyBookBarData;
};

export default function StickyBookBar({ data }: Props) {
  const [visible, setVisible] = useState(false);
  const { isFavorite, toggleFavorite, canAdd } = useFavorites();
  const active = isFavorite(data.propertyId);
  const disabled = !active && !canAdd;

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-background-50/95 backdrop-blur-md border-t border-background-200/70 px-5 py-3 md:py-4">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
        <div className="hidden sm:block">
          <p className="font-heading text-lg text-foreground-950 leading-tight">{data.propertyName}</p>
          <p className="text-xs text-foreground-500 uppercase tracking-[0.2em]">
            {data.location} &middot; {data.bedrooms} &middot; {data.sleeps}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto sm:ml-0">
          <button
            type="button"
            onClick={() => toggleFavorite(data.propertyId)}
            disabled={disabled}
            aria-pressed={active}
            aria-label={active ? 'Remove from saved properties' : 'Save this property'}
            title={disabled ? 'You can compare up to 3 properties' : active ? 'Remove from saved' : 'Save this property'}
            className={`w-10 h-10 flex items-center justify-center rounded-lg border bg-[#EFE4D6] text-foreground-700 hover:border-accent-300 hover:text-accent-700 cursor-pointer transition-colors ${
              active ? 'border-accent-300 text-accent-600 bg-accent-50' : 'border-background-300'
            } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <i className={`ri-${active ? 'heart-3-fill' : 'heart-3-line'} text-lg`} />
          </button>
          <button
            type="button"
            onClick={openPropertyGuestGuide}
            className="text-sm text-foreground-800 border border-[#DED0BE] bg-[#EFE4D6] hover:bg-[#E9DCCB] hover:border-[#B99F87] px-3 sm:px-4 py-2.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
          >
            Ask a question
          </button>
          <a
            href="#booking"
            className="relative inline-flex items-center gap-2 sm:gap-3 bg-accent-500 text-background-50 hover:bg-accent-600 pl-3 pr-3 sm:pl-4 sm:pr-5 py-3 sm:py-2.5 rounded-lg text-xs sm:text-sm cursor-pointer transition-colors whitespace-nowrap shadow-[0_14px_30px_rgba(0,0,0,0.18)]"
          >
            <span className="-mt-4 sm:-mt-5 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-[#EFE4D6] text-accent-700 border border-accent-200 shadow-sm">
              <i className="ri-calendar-check-line text-base sm:text-lg" />
            </span>
            Book This Property
          </a>
        </div>
      </div>
    </div>
  );
}
